import type { ButtonSize, ButtonVariant } from '../../../../../../mads/Button';

import { OGWrappedApplyButton } from './ApplyButton';
import { OGWrappedDisclaimerViewJobButton } from './DisclaimerViewJobButton';
import { Box, type BoxStyle } from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import { useLogErrorOnce } from '../../../../../../shared/logging/useLogErrorOnce';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { useIsDesktop } from '../../../../context/ViewJobScreenDisplay';
import { useFullScreenVJActive } from '../../../../hooks/useFullScreenVJActive';
import { useIsSticky } from '../../../../util/useIsSticky';

import { colors } from '@indeed/ifl-tokens';
import React from 'react';
import { StyleSheet } from 'react-native';


export type ViewJobButtonLinkContainerProps = {
    disclaimer?: string;
    hasLoggedSeen: boolean;
    setHasLoggedSeen: (value: boolean) => void;
    buttonSize?: ButtonSize | undefined;
    buttonVariant?: ButtonVariant | undefined;
    containerStyle?: BoxStyle | undefined;
    onApplyPress?: () => void;
    shouldHaveShadow?: boolean;
};

function ViewJobButtonLinkContainer({
    disclaimer,
    hasLoggedSeen,
    setHasLoggedSeen,
    buttonSize,
    buttonVariant = 'primary',
    onApplyPress,
    shouldHaveShadow: shouldHaveShadowProp,
}: ViewJobButtonLinkContainerProps) {
    const i18n = useI18n();

    const isSticky = useIsSticky();
    const isFullScreenVJActive = useFullScreenVJActive();
    const isDesktop = useIsDesktop();

    const hasDisclaimer = !!disclaimer && !isDesktop;
    return (
        <Box
            sx={[
                hasDisclaimer && isSticky && styles.disclaimerSticky,
                hasDisclaimer && !isSticky && styles.disclaimerNotSticky,
            ]}
        >
            {/* TODO: WENG-1507 Disclaimer should render inside of CTAEnticerBanner */}
            {hasDisclaimer && <OGWrappedDisclaimerViewJobButton />}

            <OGWrappedApplyButton
                testID="viewjob-apply"
                newTabLabel={i18n.gettext('opens in a new tab')}
                isLoading={false}
                shouldHaveShadow={shouldHaveShadowProp ?? (!disclaimer && !isFullScreenVJActive)}
                hasLoggedSeen={hasLoggedSeen}
                setHasLoggedSeen={setHasLoggedSeen}
                buttonSize={buttonSize}
                buttonVariant={buttonVariant}
                onApplyPress={onApplyPress}
            />
        </Box>
    );
}

export function OGWrappedViewJobButtonLinkContainer({
    hasLoggedSeen,
    setHasLoggedSeen,
    buttonSize,
    buttonVariant = 'primary',
    onApplyPress,
    shouldHaveShadow,
}: {
    hasLoggedSeen: boolean;
    setHasLoggedSeen: (value: boolean) => void;
    buttonSize?: ButtonSize | undefined;
    buttonVariant?: ButtonVariant | undefined;
    onApplyPress?: () => void;
    shouldHaveShadow?: boolean;
}) {
    const ogModel = useViewjobOneGraphModel();
    const logErrorOnce = useLogErrorOnce();

    if (ogModel == null) {
        logErrorOnce(
            'Apply button missing: ViewJobButtonLinkContainer not rendered due to missing ogModel',
        );
        return null;
    }

    return (
        <ViewJobButtonLinkContainer
            disclaimer={ogModel.job?.viewJobDisclaimer}
            hasLoggedSeen={hasLoggedSeen}
            setHasLoggedSeen={setHasLoggedSeen}
            buttonSize={buttonSize}
            buttonVariant={buttonVariant}
            onApplyPress={onApplyPress}
            shouldHaveShadow={shouldHaveShadow}
        />
    );
}

const styles = StyleSheet.create({
    disclaimerSticky: {
        borderTopWidth: 1,
        borderTopColor: colors.neutral['300'],
    },
    disclaimerNotSticky: {
        borderTopWidth: 1,
        borderTopColor: 'transparent',
    },
});
